// Geometry of the 270° temperature ring, the same scale as the round display (firmware RoundDisplayUi.cpp,
// gaugeAngle, Dominik's choice of 01.10.2026): setpoint at 12 o'clock, room temperature at the left end, the
// last 5 K below and above the setpoint get 60° each, beyond that up to setpoint + 30 K at the right end.
// Angles in degrees, 0 = 12 o'clock, clockwise positive.

export const GAUGE_START = -135;
export const GAUGE_END = 135;
const AMBIENT = 20;
const FINE_RANGE = 5;
const FINE_ANGLE = 60;
const ABOVE_RANGE = 25;

const clamp = (x: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, x));

export function gaugeAngle(temperature: number, setpoint: number): number {
  const d = temperature - setpoint;
  if (Math.abs(d) <= FINE_RANGE) return (d / FINE_RANGE) * FINE_ANGLE;
  if (d < 0) {
    const coarse = Math.max(setpoint - FINE_RANGE - AMBIENT, 1);
    return -FINE_ANGLE - clamp((-FINE_RANGE - d) / coarse, 0, 1) * (GAUGE_END - FINE_ANGLE);
  }
  return FINE_ANGLE + clamp((d - FINE_RANGE) / ABOVE_RANGE, 0, 1) * (GAUGE_END - FINE_ANGLE);
}

/** Angles of the minor ticks (every kelvin within ±5 K of the setpoint, the setpoint itself excluded). */
export function minorTickAngles(): number[] {
  const out: number[] = [];
  for (let k = -FINE_RANGE; k <= FINE_RANGE; k++) if (k !== 0) out.push((k / FINE_RANGE) * FINE_ANGLE);
  return out;
}

/** the ring's colour: ready, heating or cooling as core/screens/ready.ts says it, idle without a reading */
export type GaugeTone = 'heat' | 'ready' | 'cool' | 'idle';

/** Point on the ring for an angle (0 = top), in SVG coordinates. */
export function polar(cx: number, cy: number, r: number, angle: number): { x: number; y: number } {
  const rad = (angle * Math.PI) / 180;
  return { x: round(cx + r * Math.sin(rad)), y: round(cy - r * Math.cos(rad)) };
}

/** SVG path of an arc from angle a0 to a1 (clockwise). */
export function arcPath(cx: number, cy: number, r: number, a0: number, a1: number): string {
  const p0 = polar(cx, cy, r, a0);
  const p1 = polar(cx, cy, r, a1);
  const large = a1 - a0 > 180 ? 1 : 0;
  return `M ${p0.x} ${p0.y} A ${r} ${r} 0 ${large} 1 ${p1.x} ${p1.y}`;
}

const round = (x: number) => Math.round(x * 100) / 100;
